import { STREET_SURVEY_DIRECTION_LABELS } from '@citizen-walk/shared-types';
import { useFetchStreetViewImages } from './useFetchStreetViewImages';

/**
 * 「新規地点を解析」フォームで座標選択直後、AI解析を実行する前に Street View 4方向画像を
 * プレビュー表示する (docs/25 Increment 3 派生)。まだ post が存在しない座標のため、
 * `fetchStreetViewImages` callable を lat/lng 指定で呼び panoId をサーバ側で解決する
 * (`useFetchStreetViewImages` の lat/lng 対応経路)。画像は表示のみで保存しない (ToS §9-1)。
 */

/** 方向の表示名 (shared-types の定義を使う、docs/98 R-98 低)。 */
const DIRECTION_LABELS = STREET_SURVEY_DIRECTION_LABELS;

export interface StreetSurveyNewPointPreviewProps {
  districtId: string;
  lat: number;
  lng: number;
  baseHeading: number;
}

export function StreetSurveyNewPointPreview({
  districtId,
  lat,
  lng,
  baseHeading,
}: StreetSurveyNewPointPreviewProps) {
  const headings = [0, 90, 180, 270].map((offset) => (baseHeading + offset) % 360);
  const imagesState = useFetchStreetViewImages(districtId, { lat, lng }, headings);

  if (imagesState.status === 'idle') return null;

  if (imagesState.status === 'loading') {
    return <p className="text-xs text-slate-500 mt-2">Street View プレビューを読込中…</p>;
  }

  if (imagesState.status === 'error') {
    return (
      <p className="text-xs text-amber-700 mt-2">
        Street View プレビューを取得できませんでした: {imagesState.error}
      </p>
    );
  }

  return (
    <div className="mt-3">
      <p className="text-xs font-bold text-slate-600 mb-1">Street View プレビュー（4方向）</p>
      {/* 解析済み投稿の2D画像ボード (StreetSurveyImageBoard.tsx) と同じ2x2レイアウトに揃え、
          1行4枚並べより各画像の表示幅を確保する (視認性改善)。 */}
      <div className="grid grid-cols-2 gap-2">
        {imagesState.images.map((img, i) => (
          <div
            key={img.heading}
            className="relative rounded-lg border border-slate-200 overflow-hidden"
          >
            <div className="absolute top-1 left-1 z-10 bg-black/60 text-white text-[11px] px-1.5 py-0.5 rounded">
              {DIRECTION_LABELS[i] ?? ''} ({Math.round(img.heading)}°)
            </div>
            <img
              src={`data:${img.mimeType};base64,${img.data}`}
              alt={`${DIRECTION_LABELS[i] ?? ''} (${Math.round(img.heading)}°)`}
              className="w-full aspect-[4/3] object-cover"
            />
          </div>
        ))}
      </div>
    </div>
  );
}
